import {
  Component,
  DestroyRef,
  HostListener,
  LOCALE_ID,
  NgZone,
  OnInit
} from '@angular/core';
import {
  NavigationEnd,
  RouteConfigLoadStart,
  Router,
  RouterOutlet
} from '@angular/router';
import { CommonModule, registerLocaleData } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';

import localeUk from '@angular/common/locales/uk';
import localeEn from '@angular/common/locales/en';
import localeCs from '@angular/common/locales/cs';
import localeRuKZ from '@angular/common/locales/ru-KZ';
import localePl from '@angular/common/locales/pl';
import { MatProgressBar } from '@angular/material/progress-bar';

import { OAuthService } from 'angular-oauth2-oidc';
import { filter, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import 'moment/locale/uk';

import { environment } from '../environments/environment';
import { PreLoaderStore } from '@app/components/header/store/pre-loader.store';
import { UserService } from '@app/services/user.service';
import {
  SignupActiveScreen,
  SignupService
} from '@app/pages/signup/services/signup.service';
import { authConfig, restrictedPages } from '@app/app.config';
import { appActions } from '@app/core/store/app.actions';
import { MonitorNetworkService } from '@app/services/monitor-network.service';
import {
  MatSnackBar,
  MatSnackBarRef,
  TextOnlySnackBar
} from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NetworkCheckOfflineModalComponent } from '@app/shared/components/network-check-offline-modal/network-check-offline-modal.component';
import { User } from '@app/shared/models';

registerLocaleData(localeUk, 'uk');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeCs, 'cs');
registerLocaleData(localeRuKZ, 'ru-KZ');
registerLocaleData(localePl, 'pl');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  providers: [
    {
      provide: LOCALE_ID,
      useFactory: (translate: TranslateService) => {
        const lang = translate.currentLang;
        return lang === 'kz' ? 'ru-KZ' : lang;
      },
      deps: [TranslateService]
    },
    MonitorNetworkService
  ],
  imports: [RouterOutlet, MatProgressBar, CommonModule, TranslatePipe]
})
export class AppComponent implements OnInit {
  public isVisibleOnLoading$ = this.preLoaderStore.isVisibleOnLoading$;

  public showRotateError = false;
  public projectLanguages = ['uk', 'cs', 'en', 'kz', 'pl'];

  private readonly restrictedPages = restrictedPages;
  private readonly authConfig = authConfig;

  private isMobile = false;
  private currentUrl = '';
  private firstCheck = true;
  private offlineRef?: MatDialogRef<NetworkCheckOfflineModalComponent> | null;
  private user: User;

  constructor(
    private router: Router,
    private oauthService: OAuthService,
    private preLoaderStore: PreLoaderStore,
    private userService: UserService,
    private destroyRef: DestroyRef,
    private signupService: SignupService,
    private translate: TranslateService,
    private meta: Meta,
    private store: Store,
    private networkService: MonitorNetworkService,
    private dialog: MatDialog,
    private ngZone: NgZone
  ) {}

  @HostListener('window:orientationchange', ['$event'])
  onOrientationChange() {
    this.checkOrientationMobile();
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (this.isBrowserOrWindowsZoom(1.25)) {
      document.documentElement.style.fontSize = '11.5px';
    } else if (this.isBrowserOrWindowsZoom(1.5)) {
      document.documentElement.style.fontSize = '9.5px';
    } else {
      document.documentElement.style.fontSize = '14px';
    }
  }

  ngOnInit(): void {
    this.streamToLanguageQuery();
    this.streamRouterEvents();
    this.oauthService.configure(this.authConfig);
    this.oauthService.setStorage(localStorage);
    this.setSomeSettings();
    this.streamToChangeNetwork();

    if (
      window.location.href.includes(
        'procurement/suppliers-office/procurement-list'
      ) &&
      !this.oauthService.hasValidAccessToken()
    ) {
      return;
    }

    if (this.oauthService.hasValidAccessToken()) {
      this.getUser();
    } else {
      this.oauthService.refreshToken().then(
        () => {
          this.getUser();
        },
        () => {
          this.router.navigate(['/signin'], { queryParamsHandling: 'merge' });
        }
      );
    }

    if (this.isBrowserOrWindowsZoom(1.25)) {
      document.documentElement.style.fontSize = '11.5px';
    } else if (this.isBrowserOrWindowsZoom(1.5)) {
      document.documentElement.style.fontSize = '9.5px';
    } else {
      document.documentElement.style.fontSize = '14px';
    }
  }

  private isBrowserOrWindowsZoom(
    expectedZoom: number,
    tolerance: number = 0.05
  ): boolean {
    const platform = navigator.platform.toLowerCase();
    const isWindows = platform.includes('win');
    const isNotMac = !platform.includes('mac');
    const isNotRetina = window.devicePixelRatio <= 1.5;

    const actualZoom = window.devicePixelRatio;
    const isZoomed = Math.abs(actualZoom - expectedZoom) <= tolerance;

    return isWindows && isNotMac && isNotRetina && isZoomed;
  }

  private streamToLanguageQuery(): void {
    const lang = this.getValidLanguage(localStorage.getItem('currentLanguage'));

    this.store.dispatch(appActions.setLang({ lang }));
    this.translate.use(lang);
  }

  private getValidLanguage(storedLang: string | null): string {
    let lang = storedLang || this.translate.getBrowserCultureLang() || 'uk';
    if (lang === 'kk') lang = 'kz';
    if (!this.projectLanguages.includes(lang)) lang = 'uk';
    return lang;
  }

  private startTokenRefreshCheck(): void {
    const expiration = this.oauthService.getAccessTokenExpiration();
    const refreshTime = expiration - Date.now() - 60000;

    if (refreshTime > 0) {
      setTimeout(() => this.refreshToken(), refreshTime);
    }
  }

  private refreshToken(): void {
    this.oauthService.refreshToken().then(
      () => {
        this.startTokenRefreshCheck();
      },
      () => {
        this.router.navigate(['/signin'], { queryParamsHandling: 'merge' });
      }
    );
  }

  private getUser(): void {
    this.userService
      .getUser()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(data => (this.user = data)),
        filter(user => !user.companyId)
      )
      .subscribe(() => {
        this.router.navigate(['/signup'], { queryParamsHandling: 'merge' });
        this.signupService.signupActiveScreen.next(
          SignupActiveScreen.RegisterCompany
        );
      });
  }

  private streamRouterEvents(): void {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this.currentUrl = (event as NavigationEnd).url;
        if (event instanceof RouteConfigLoadStart) {
          this.preLoaderStore.showLoader();
        } else if (event instanceof NavigationEnd) {
          this.preLoaderStore.hideLoader();
        }

        this.isMobile = this.checkIfMobile();
        this.checkOrientationMobile();

        window.addEventListener('orientationchange', () => {
          this.checkOrientationMobile();
        });
      });
  }

  private checkIfMobile(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|ipod|android|webos|blackberry|iemobile|opera mini|mobile|tablet/.test(
      userAgent
    );
  }

  private checkOrientationMobile(): void {
    if (this.isMobile) {
      const orientation = screen.orientation?.angle || window.orientation;
      if (
        this.restrictedPages.includes(this.currentUrl) &&
        (orientation === 90 || orientation === -90)
      ) {
        this.showRotateError = true;
      } else {
        this.showRotateError = false;
      }
    }
  }

  private setSomeSettings(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const currentUrl = this.router.url;
        const isRestrictedPage = this.restrictedPages.some(page =>
          currentUrl.includes(page)
        );

        let viewportContent = environment.viewport;

        if (isRestrictedPage) {
          viewportContent =
            'width=device-width, initial-scale=1.0, user-scalable=no, maximum-scale=1.0, minimum-scale=1.0';
        }

        if (document.querySelector('meta[name="viewport"]')) {
          this.meta.updateTag({ name: 'viewport', content: viewportContent });
        } else {
          this.meta.addTag({ name: 'viewport', content: viewportContent });
        }
      });
  }

  private streamToChangeNetwork(): void {
    this.networkService.isOnline$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isOnline => {
        this.ngZone.run(() => {
          if (this.firstCheck) {
            this.firstCheck = false;
            return;
          }

          if (!isOnline) {
            const newRecord = {
              time: new Date().toISOString(),
              data: {
                startDateOffline: new Date(),
                userId: this.user.id,
                userName: `${this.user.firstName} ${this.user.lastName}`,
                isRead: false
              }
            };

            const stored = localStorage.getItem('offlineEvents');
            const events: any[] = stored ? JSON.parse(stored) : [];

            events.push(newRecord);

            localStorage.setItem('offlineEvents', JSON.stringify(events));

            if (!this.offlineRef) {
              this.offlineRef = this.dialog.open(
                NetworkCheckOfflineModalComponent,
                {
                  disableClose: true
                }
              );

              this.offlineRef.afterClosed().subscribe(() => {
                const storedAgain = localStorage.getItem('offlineEvents');
                if (storedAgain) {
                  const updated = JSON.parse(storedAgain);
                  if (updated.length > 0) {
                    updated[updated.length - 1].data.isRead = true;
                  }
                  localStorage.setItem(
                    'offlineEvents',
                    JSON.stringify(updated)
                  );
                }

                this.offlineRef = null;
              });
            }
          } else {
            if (this.offlineRef) {
              this.offlineRef.close();
              this.offlineRef = null;
            }
          }
        });
      });
  }
}
